import type {
    LogChannel,
    LogEntry,
    LogSearchResponse,
    LogUser,
} from "../../../core/types.js";
import { SharedLogSearchRequestQueue } from "../../../utils/logSearchRequests.js";

type LogSearchQueue = {
    fetchJson<TPayload extends Record<string, unknown>>(url: string, options?: RequestInit): Promise<TPayload>;
};

type MemberRoleContextResponse = LogSearchResponse & {
    result: LogEntry[];
};

export type MemberRoleReference = {
    id: string;
    name?: string | null;
};

export type MemberRoleCommandDetection = {
    confidence: "high" | "medium" | "low";
    kind: "command" | "bot_log" | "related_message";
    pattern?: string;
    log: LogEntry;
};

export type MemberRoleContextResult = {
    messages: LogEntry[];
    members: LogUser[];
    channels: LogChannel[];
    command: MemberRoleCommandDetection | null;
};

type MemberRoleAction = "add" | "remove";

const MemberRoleContextWindowMs = 10_000;
const MemberRoleContextLimit = 5;
const MemberRoleContextLogTypes = [ "message_create", "message_edit" ];
const AddRoleCommandRegex = "(^|[^a-z0-9_])[./!&?+]?(role|addrole|giverole|rank|mute|vip|verify|reactionrole|rr)([^a-z0-9_]|$)";
const RemoveRoleCommandRegex = "(^|[^a-z0-9_])[./!&?+]?(role|removerole|delrole|unrole|unrank|unmute|unvip|unverify|reactionrole|rr)([^a-z0-9_]|$)";
const BotRoleLogRegex = "(role|rôle|rank|mute|unmute|vip|verify|reaction)";
const AddRoleCommandPatterns = [
    { label: ".role", pattern: /(^|\s)[./!&?+]?role\b/i },
    { label: ".addrole", pattern: /(^|\s)[./!&?+]?addrole\b/i },
    { label: ".giverole", pattern: /(^|\s)[./!&?+]?giverole\b/i },
    { label: ".rank", pattern: /(^|\s)[./!&?+]?rank\b/i },
    { label: ".mute", pattern: /(^|\s)[./!&?+]?mute\b/i },
    { label: ".vip", pattern: /(^|\s)[./!&?+]?vip\b/i },
    { label: ".verify", pattern: /(^|\s)[./!&?+]?verify\b/i },
    { label: ".reactionrole", pattern: /(^|\s)[./!&?+]?reactionrole\b/i },
    { label: ".rr", pattern: /(^|\s)[./!&?+]?rr\b/i },
] as const;
const RemoveRoleCommandPatterns = [
    { label: ".role", pattern: /(^|\s)[./!&?+]?role\b/i },
    { label: ".removerole", pattern: /(^|\s)[./!&?+]?removerole\b/i },
    { label: ".delrole", pattern: /(^|\s)[./!&?+]?delrole\b/i },
    { label: ".unrole", pattern: /(^|\s)[./!&?+]?unrole\b/i },
    { label: ".unrank", pattern: /(^|\s)[./!&?+]?unrank\b/i },
    { label: ".unmute", pattern: /(^|\s)[./!&?+]?unmute\b/i },
    { label: ".unvip", pattern: /(^|\s)[./!&?+]?unvip\b/i },
    { label: ".unverify", pattern: /(^|\s)[./!&?+]?unverify\b/i },
    { label: ".reactionrole", pattern: /(^|\s)[./!&?+]?reactionrole\b/i },
    { label: ".rr", pattern: /(^|\s)[./!&?+]?rr\b/i },
] as const;

function createJsonPostOptions(body: Record<string, unknown>): RequestInit {
    return {
        headers: {
            "content-type": "application/json",
        },
        body    : JSON.stringify(body),
        method  : "POST",
    };
}

function quoteExpressionString(value: string): string {
    return JSON.stringify(value);
}

function buildTypeSet(values: readonly string[]): string {
    return values.map(quoteExpressionString).join(", ");
}

function getAction(log: LogEntry): MemberRoleAction {
    return log.type === "member_role_remove"
        ? "remove"
        : "add";
}

function getActionCommandRegex(action: MemberRoleAction): string {
    return action === "remove"
        ? RemoveRoleCommandRegex
        : AddRoleCommandRegex;
}

function getActionCommandPatterns(action: MemberRoleAction) {
    return action === "remove"
        ? RemoveRoleCommandPatterns
        : AddRoleCommandPatterns;
}

function collectStrings(value: unknown, result: string[]): void {
    if (typeof value === "string") {
        result.push(value);
        return;
    }

    if (Array.isArray(value)) {
        for (const item of value)
            collectStrings(item, result);
        return;
    }

    if (!value || typeof value !== "object")
        return;

    for (const item of Object.values(value))
        collectStrings(item, result);
}

function getSearchableLogText(log: LogEntry): string {
    const values: string[] = [];

    collectStrings(log.extra, values);

    return values.join("\n");
}

function getSafeWindow(createdAt: number): { after: number; before: number } {
    const now = Date.now();
    const before = Math.min(createdAt + MemberRoleContextWindowMs, now);

    return {
        after: Math.min(Math.max(0, createdAt - MemberRoleContextWindowMs), Math.max(0, before - 1)),
        before,
    };
}

function buildRoleReferenceClauses(roles: readonly MemberRoleReference[]): string[] {
    const clauses: string[] = [];

    for (const role of roles) {
        if (role.id)
            clauses.push(`contains(extra, ${quoteExpressionString(role.id)})`);

        if (role.name?.trim())
            clauses.push(`contains(extra, ${quoteExpressionString(role.name.trim())})`);
    }

    return clauses;
}

function buildMemberRoleContextExpression(log: LogEntry, currentBotId: string, roles: readonly MemberRoleReference[]): string | null {
    const target = log.target || log.executor;

    if (!target)
        return null;

    const action = getAction(log);
    const commandClause = `match(lower(extra.content), ${quoteExpressionString(getActionCommandRegex(action))})`;
    const botLogClause = [
        `executor == ${quoteExpressionString(currentBotId)}`,
        `match(lower(extra), ${quoteExpressionString(BotRoleLogRegex)})`,
    ].join(" and ");
    const roleClauses = buildRoleReferenceClauses(roles);
    const relatedClauses = [
        commandClause,
        botLogClause,
        ...roleClauses,
    ];

    return [
        `type in { ${buildTypeSet(MemberRoleContextLogTypes)} }`,
        ` and contains(extra, ${quoteExpressionString(target)})`,
        ` and (${relatedClauses.join(" or ")})`,
    ].join("");
}

async function fetchMemberRoleContextLogs({
    after,
    before,
    expression,
    orderBy = "ASC",
    queue,
    signal,
}: {
    after?: number;
    before?: number;
    expression: string;
    orderBy?: "ASC" | "DESC";
    queue: LogSearchQueue;
    signal?: AbortSignal;
}): Promise<MemberRoleContextResponse> {
    const params = new URLSearchParams({
        limit: String(MemberRoleContextLimit),
        orderBy,
    });

    if (after !== undefined)
        params.set("after", String(after));

    if (before !== undefined)
        params.set("before", String(before));

    const body = await queue.fetchJson<MemberRoleContextResponse>(`/api/logs?${params.toString()}`, {
        ...createJsonPostOptions({
            mode  : "expression",
            query : expression,
        }),
        signal,
    });

    if (body.error)
        throw new Error(body.error);

    return body;
}

function mentionsTarget(content: string, targetId: string | null | undefined): boolean {
    return targetId ? content.includes(targetId) : false;
}

function mentionsRole(content: string, roles: readonly MemberRoleReference[]): boolean {
    const normalized = content.toLowerCase();

    return roles.some((role) => {
        if (role.id && content.includes(role.id))
            return true;

        return Boolean(role.name?.trim() && normalized.includes(role.name.trim().toLowerCase()));
    });
}

function getMatchedCommandPattern(content: string, action: MemberRoleAction): string | null {
    for (const commandPattern of getActionCommandPatterns(action)) {
        if (commandPattern.pattern.test(content))
            return commandPattern.label;
    }

    return null;
}

function findMemberRoleCommand(
    logs: readonly LogEntry[],
    targetId: string | null | undefined,
    roles: readonly MemberRoleReference[],
    botId: string,
    action: MemberRoleAction,
): MemberRoleCommandDetection | null {
    for (const log of logs) {
        const content = getSearchableLogText(log);

        if (!mentionsTarget(content, targetId))
            continue;

        const hasRole = mentionsRole(content, roles);
        const matchedCommand = getMatchedCommandPattern(content, action);

        if (matchedCommand && hasRole && log.executor !== botId)
            return {
                confidence : "high",
                kind       : "command",
                pattern    : matchedCommand,
                log,
            };

        if (matchedCommand && log.executor !== botId)
            return {
                confidence : "medium",
                kind       : "command",
                pattern    : matchedCommand,
                log,
            };

        if (log.executor === botId && (hasRole || new RegExp(BotRoleLogRegex, "i").test(content)))
            return {
                confidence : "medium",
                kind       : "bot_log",
                log,
            };

        if (hasRole) {
            return {
                confidence : log.executor === botId ? "low" : "medium",
                kind       : "related_message",
                log,
            };
        }
    }

    return null;
}

export async function loadMemberRoleContext(
    log: LogEntry,
    currentBotId: string,
    roles: readonly MemberRoleReference[],
    {
        queue = SharedLogSearchRequestQueue,
        signal,
    }: {
        queue?: LogSearchQueue;
        signal?: AbortSignal;
    } = {},
): Promise<MemberRoleContextResult> {
    const expression = buildMemberRoleContextExpression(log, currentBotId, roles);
    const action = getAction(log);

    if (!expression)
        return { messages: [], members: [], channels: [], command: null };

    const window = getSafeWindow(log.createdAt);
    let body = await fetchMemberRoleContextLogs({
        after      : window.after,
        before     : window.before,
        expression,
        queue,
        signal,
    });
    let logs = (body.result ?? [])
        .sort((left, right) => left.createdAt - right.createdAt);
    let command = findMemberRoleCommand(logs, log.target || log.executor, roles, currentBotId, action);

    if (!command) {
        const fallbackBody = await fetchMemberRoleContextLogs({
            expression,
            orderBy    : "DESC",
            queue,
            signal,
        });
        const fallbackLogsByRecency = fallbackBody.result ?? [];
        const fallbackCommand = findMemberRoleCommand(fallbackLogsByRecency, log.target || log.executor, roles, currentBotId, action);

        if (fallbackCommand) {
            body = fallbackBody;
            command = fallbackCommand;
            logs = fallbackLogsByRecency.sort((left, right) => left.createdAt - right.createdAt);
        }
    }

    return {
        messages    : logs.slice(0, MemberRoleContextLimit),
        members     : body.members ?? [],
        channels    : body.channels ?? [],
        command,
    };
}
